import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import './Detail.css'
import Modal from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faShoppingCart, faExternalLinkAlt, faStar, faHeart } from '@fortawesome/free-solid-svg-icons';
import whatsappIcon from '../../images/wpp.png';
import { useNavigate, useLocation } from "react-router-dom";
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { toast } from 'react-toastify';
import DetailLoading from "../DetailLoading/DetailLoading";
import { useCatalogData } from '../CatalogData/CatalogDataProvider';
import moneda from '../moneda';

SwiperCore.use([Navigation, Pagination, Autoplay]);

const FAVORITES_STORAGE_KEY = 'favoritos';
const CART_STORAGE_KEY = 'cart';

const leerListaGuardada = (storageKey) => {
    try {
        const storedValue = JSON.parse(localStorage.getItem(storageKey));
        return Array.isArray(storedValue) ? storedValue : [];
    } catch {
        return [];
    }
};

const combinarItemsUnicos = (...listas) => {
    const vistos = new Set();

    return listas.reduce((itemsUnicos, lista) => {
        const items = Array.isArray(lista) ? lista : [];

        items.forEach((item) => {
            if (typeof item !== 'string' && typeof item !== 'number') {
                return;
            }

            const itemLimpio = String(item).trim();
            const clave = itemLimpio.toLowerCase();

            if (itemLimpio && !vistos.has(clave)) {
                vistos.add(clave);
                itemsUnicos.push(itemLimpio);
            }
        });

        return itemsUnicos;
    }, []);
};

export default function Detail() {
    const navigate = useNavigate();
    const swiperRef = useRef(null);
    const { idProducto } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImage, setModalImage] = useState("");
    const [cantidad, setCantidad] = useState(1);
    const [favoritos, setFavoritos] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const location = useLocation();
    const categoriasInputRef = useRef(null);
    const [fixedCategories, setFixedCategories] = useState(false);
    const {
        productos,
        categorias,
        subcategorias,
        tiendaPrincipal,
        status,
        errors,
        ensureProductos,
        ensureCategorias,
        ensureSubcategorias,
        ensureTienda,
        retryProductos,
        retryCategorias,
        retrySubcategorias,
        retryTienda,
    } = useCatalogData();
    const producto = useMemo(() => (
        productos.find(item => String(item.idProducto) === String(idProducto)) || null
    ), [idProducto, productos]);
    const categoria = useMemo(() => (
        categorias.find(item => String(item.idCategoria) === String(producto?.idCategoria)) || null
    ), [categorias, producto?.idCategoria]);
    const subcategoria = useMemo(() => (
        subcategorias.find(item => (
            String(item.idSubCategoria) === String(producto?.idSubCategoria)
        )) || null
    ), [producto?.idSubCategoria, subcategorias]);
    const items = useMemo(() => combinarItemsUnicos([
        producto?.item1,
        producto?.item2,
        producto?.item3,
        producto?.item4,
        producto?.item5,
        producto?.item6,
        producto?.item7,
        producto?.item8,
        producto?.item9,
        producto?.item10,
    ]), [producto]);
    const tieneItems = producto?.verItems === 'Si';
    const loading = status.productos === 'idle' || status.productos === 'loading';
    const productError = status.productos === 'error' || Boolean(errors.productos);
    const supportingDataError = (
        status.categorias === 'error'
        || status.subcategorias === 'error'
        || status.tienda === 'error'
    );
    const whatsappDisponible = Boolean(tiendaPrincipal?.telefono);

    useEffect(() => {
        if (status.productos === 'idle') {
            ensureProductos().catch(() => {});
        }
        if (status.categorias === 'idle') {
            ensureCategorias().catch(() => {});
        }
        if (status.subcategorias === 'idle') {
            ensureSubcategorias().catch(() => {});
        }
        if (status.tienda === 'idle') {
            ensureTienda().catch(() => {});
        }
    }, [
        ensureCategorias,
        ensureProductos,
        ensureSubcategorias,
        ensureTienda,
        status.categorias,
        status.productos,
        status.subcategorias,
        status.tienda,
    ]);

    useEffect(() => {
        setCantidad(1);
        setSelectedItems([]);
        setIsModalOpen(false);
        setModalImage("");
        setFavoritos(leerListaGuardada(FAVORITES_STORAGE_KEY));
        window.scrollTo(0, 0);
    }, [idProducto]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleScroll = () => {
        if (categoriasInputRef.current) {
            if (window.scrollY > categoriasInputRef.current.offsetTop) {
                setFixedCategories(true);
            } else {
                setFixedCategories(false);
            }
        }
    };

    const handleCheckboxChange = (item) => {
        if (selectedItems.includes(item)) {
            setSelectedItems(selectedItems.filter(i => i !== item)); // Deselecciona si ya estaba seleccionado
        } else {
            setSelectedItems([...selectedItems, item]); // Agrega el item si no estaba seleccionado
        }
    };

    const handleRetryProducts = () => {
        retryProductos().catch(() => {});
    };

    const handleRetrySupportingData = () => {
        if (status.categorias === 'error') {
            retryCategorias().catch(() => {});
        }
        if (status.subcategorias === 'error') {
            retrySubcategorias().catch(() => {});
        }
        if (status.tienda === 'error') {
            retryTienda().catch(() => {});
        }
    };

    function handleCompartirClick() {
        if (navigator.share) {
            navigator.share({
                title: document.title,
                text: 'Echa un vistazo a este producto',
                url: window.location.href,
            })
                .catch((error) => console.error('Error al compartir:', error));
        } else {
            console.error('La API de compartir no está disponible en este navegador.');
        }
    }

    const handleWhatsappMessage = () => {
        if (!whatsappDisponible) {
            return;
        }

        const phoneNumber = tiendaPrincipal.telefono;
        const removeAccents = (str) => {
            return str.normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // Normaliza y elimina acentos
        };

        const title = encodeURIComponent(
            removeAccents(String(producto?.titulo || '')).replace(/\s+/g, '-')
        );

        const formattedPrice = Number(producto?.precio).toLocaleString('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        // Verifica si hay items seleccionados
        const selectedItemsText = selectedItems.length > 0
            ? selectedItems.join(', ')
            : "";

        const message = `Hola 🌟, quisiera más información sobre\n\n✅ *${title}*\n     ${selectedItemsText}\n     ${moneda} ${formattedPrice}`;

        const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
        //const whatsappUrl = `https://api.whatsapp.com/send?phone=${+573004085041}&text=${encodeURIComponent(message)}`;

        window.open(whatsappUrl, '_blank');
    };

    const goBack = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const addToCart = () => {
        if (producto) {
            const stockDisponible = Number(producto.stock);
            if (!Number.isInteger(stockDisponible) || stockDisponible < 1) {
                toast.error('No hay stock', { autoClose: 400 });
                return;
            }

            const cart = leerListaGuardada(CART_STORAGE_KEY);
            const existingItemIndex = cart.findIndex(item =>
                Number(item.idProducto) === Number(producto.idProducto)
            );
            const cantidadActual = existingItemIndex !== -1
                ? Number(cart[existingItemIndex].cantidad) || 0
                : 0;

            if (cantidadActual + cantidad > stockDisponible) {
                toast.error(`Solo hay ${stockDisponible} unidades disponibles.`, { autoClose: 1200 });
                return;
            }

            const itemsToAdd = tieneItems ? selectedItems : [];

            if (existingItemIndex !== -1) {
                const existingItem = cart[existingItemIndex];
                const updatedItems = tieneItems
                    ? combinarItemsUnicos(existingItem.items, itemsToAdd)
                    : [];
                const updatedCantidad = cantidadActual + cantidad;
                cart[existingItemIndex] = { ...existingItem, items: updatedItems, cantidad: updatedCantidad };
            } else {
                cart.push({ idProducto: producto.idProducto, items: combinarItemsUnicos(itemsToAdd), cantidad });
            }

            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
            window.dispatchEvent(new Event('inksu:cart-updated'));
            toast.success('Producto agregado', { autoClose: 400 });
        }
    };

    const incrementCantidad = () => {
        const stockDisponible = Number(producto?.stock);
        if (Number.isInteger(stockDisponible) && cantidad >= stockDisponible) {
            toast.error(`Solo hay ${stockDisponible} unidades disponibles.`, { autoClose: 1200 });
            return;
        }

        setCantidad(cantidad + 1);
    };

    const decrementCantidad = () => {
        if (cantidad > 1) {
            setCantidad(cantidad - 1);
        }
    };

    const agregarAFavoritos = (idProducto) => {
        const favList = [...favoritos];
        const index = favList.indexOf(idProducto);
        if (index === -1) {
            // Si el producto no está en favoritos, lo agregamos
            favList.push(idProducto);
            setFavoritos(favList);
            localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favList));
        } else {
            // Si el producto está en favoritos, lo eliminamos
            favList.splice(index, 1);
            setFavoritos(favList);
            localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favList));
        }
    };

    if (loading) {
        return <DetailLoading />;
    }

    if (productError) {
        return (
            <section className="detail" role="alert">
                <h2>No fue posible cargar el producto.</h2>
                <p>Inténtalo nuevamente.</p>
                <button type="button" onClick={handleRetryProducts}>Reintentar</button>
            </section>
        );
    }

    if (!producto) {
        return (
            <section className="detail" role="status">
                <h2>Producto no encontrado.</h2>
                <button type="button" onClick={goBack}>Volver</button>
            </section>
        );
    }

    const primeraImagenDisponible = [
        producto.imagen1,
        producto.imagen2,
        producto.imagen3,
        producto.imagen4,
    ].find(Boolean);

    return (
        <div className="detail">
            <div className={`deFlexDetail ${fixedCategories ? 'fixedHeader' : ''}`} ref={categoriasInputRef}>
                <button type="button" className="back" onClick={goBack} aria-label="Volver"> <FontAwesomeIcon icon={faArrowLeft} /> </button>

                <div className="deFLexIcon">
                    <button
                        type="button"
                        onClick={() => agregarAFavoritos(producto.idProducto)}
                        className='favoritos-btn'
                        aria-label={favoritos.includes(producto.idProducto)
                            ? 'Quitar producto de favoritos'
                            : 'Agregar producto a favoritos'}
                    >
                        <FontAwesomeIcon icon={faHeart} style={{ color: favoritos.includes(producto.idProducto) ? 'red' : 'gray' }} />
                    </button>
                    <button type="button" className="share" onClick={handleCompartirClick} aria-label="Compartir producto"> <FontAwesomeIcon icon={faExternalLinkAlt} /> </button>
                </div>
            </div>

            <div className="detail-contain">
                <SwiperSlide id={"swiperDetail"}>
                    <Swiper
                        effect={'coverflow'}
                        grabCursor={true}
                        loop={true}
                        slidesPerView={'auto'}
                        coverflowEffect={{ rotate: 0, stretch: 0, depth: 100, modifier: 2.5 }}
                        navigation={{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }}
                        autoplay={{ delay: 3000 }} // Cambia el valor de 'delay' según tus preferencias
                        pagination={{ clickable: true }}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                    >
                        {producto.imagen1 ? (
                            <SwiperSlide>
                                <img
                                    src={producto.imagen1}
                                    alt={producto.titulo}
                                    className="imagen1"
                                    loading={producto.imagen1 === primeraImagenDisponible ? 'eager' : 'lazy'}
                                    fetchpriority={producto.imagen1 === primeraImagenDisponible ? 'high' : undefined}
                                    decoding="async"
                                    onClick={() => {
                                        setModalImage(producto.imagen1);
                                        setIsModalOpen(true);
                                    }}
                                />
                            </SwiperSlide>
                        ) : (<></>)}

                        {producto.imagen2 ? (
                            <SwiperSlide>
                                <img
                                    src={producto.imagen2}
                                    alt={producto.titulo}
                                    className="imagen2"
                                    loading={producto.imagen2 === primeraImagenDisponible ? 'eager' : 'lazy'}
                                    fetchpriority={producto.imagen2 === primeraImagenDisponible ? 'high' : undefined}
                                    decoding="async"
                                    onClick={() => {
                                        setModalImage(producto.imagen2);
                                        setIsModalOpen(true);
                                    }}
                                />
                            </SwiperSlide>
                        ) : (<></>)}

                        {producto.imagen3 ? (
                            <SwiperSlide>
                                <img
                                    src={producto.imagen3}
                                    alt={producto.titulo}
                                    className="img"
                                    loading={producto.imagen3 === primeraImagenDisponible ? 'eager' : 'lazy'}
                                    fetchpriority={producto.imagen3 === primeraImagenDisponible ? 'high' : undefined}
                                    decoding="async"
                                    onClick={() => {
                                        setModalImage(producto.imagen3);
                                        setIsModalOpen(true);
                                    }}
                                />
                            </SwiperSlide>
                        ) : (<></>)}

                        {producto.imagen4 ? (
                            <SwiperSlide>
                                <img
                                    src={producto.imagen4}
                                    alt={producto.titulo}
                                    className="imagen4"
                                    loading={producto.imagen4 === primeraImagenDisponible ? 'eager' : 'lazy'}
                                    fetchpriority={producto.imagen4 === primeraImagenDisponible ? 'high' : undefined}
                                    decoding="async"
                                    onClick={() => {
                                        setModalImage(producto.imagen4);
                                        setIsModalOpen(true);
                                    }}
                                />
                            </SwiperSlide>
                        ) : (<></>)}
                    </Swiper>
                </SwiperSlide>

                <div className="textDetail">
                    <h2 className="title">{producto.titulo}</h2>
                    <hr />
                    <div className="deFLexBuet">
                        {categoria && (
                            <h4>
                                <FontAwesomeIcon icon={faStar} />
                                {categoria.categoria}
                                {subcategoria && ` > ${subcategoria.subcategoria}`}
                            </h4>
                        )}
                        {producto.stock === 1 ? (
                            <h4 style={{ color: 'green', backgroundColor: '#ccffcc', padding: '0px 10px', borderRadius: '6px' }}>Stock</h4>
                        ) : producto.stock <= 0 ? (
                            <h4 style={{ color: 'red', backgroundColor: '#ffc1c1', padding: '0px 10px', borderRadius: '6px' }}>Agotado</h4>
                        ) : (
                            <h4 style={{ color: 'green', backgroundColor: '#ccffcc', padding: '0px 10px', borderRadius: '6px' }}>{producto.stock}</h4>
                        )}
                    </div>
                    {supportingDataError && (
                        <button type="button" onClick={handleRetrySupportingData}>
                            Reintentar información
                        </button>
                    )}

                    <div className='deFLexPrice'>
                        <h5 className="price">
                            {moneda} {String(producto?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        </h5>

                        {
                            (producto?.precioAnterior !== 0 && producto?.precioAnterior !== undefined) && (
                                <h5 className='precioTachadoDetail'> {moneda} {`${producto?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                            )
                        }
                    </div>

                    <div className="detailTrustBox" aria-label="Información de confianza para la compra">
                        <span>Compra sin crear cuenta.</span>
                        <span>Agrega al carrito y confirma tu pedido por WhatsApp.</span>
                        <span>¿No estás seguro si es compatible? Escríbenos y te asesoramos.</span>
                        <span>
                            {tieneItems
                                ? 'El precio es por unidad. Cada color o referencia seleccionada cuenta como 1 unidad. Ejemplo: Magenta + Cian + Negro = cantidad 3.'
                                : 'Disponibilidad sujeta al stock actual.'}
                        </span>
                    </div>

                    <p>{producto.descripcion}</p>
                    {
                        producto?.verItems === 'Si' && (
                            <div className="itemsDetail">
                                {items.map((item) => (
                                    item && (
                                        <label key={item}>
                                            <input
                                                type="checkbox"
                                                checked={selectedItems.includes(item)}
                                                onChange={() => handleCheckboxChange(item)}
                                            />
                                            <span>  {item}</span>
                                        </label>
                                    )
                                ))}
                            </div>
                        )
                    }

                    <div className='deFlexCart'>
                        <button type="button" onClick={decrementCantidad} aria-label="Disminuir cantidad">-</button>
                        <span>{cantidad}</span>
                        <button type="button" onClick={incrementCantidad} aria-label="Aumentar cantidad">+</button>
                    </div>
                    <div className='deFlexGoTocart'>
                        <button type="button" onClick={addToCart} className='btnAdd' aria-label="Agregar al carrito">Agregar  <FontAwesomeIcon icon={faShoppingCart} />  </button>
                        <button
                            type="button"
                            className="wpp"
                            onClick={handleWhatsappMessage}
                            disabled={!whatsappDisponible}
                            aria-label={whatsappDisponible
                                ? 'Consultar producto por WhatsApp'
                                : 'WhatsApp no disponible'}
                        >
                            WhatsApp
                            <img src={whatsappIcon} alt="whatsappIcon" />
                        </button>
                    </div>
                </div>
            </div>
            <Modal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                center
                classNames={{
                    modal: 'custom-modal',
                }}
            >
                <img src={modalImage} alt={producto.titulo} loading="lazy" decoding="async" />
            </Modal>
        </div>
    )
}
